import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { LocaleSchema } from '../../common/locale/locale';
import { AppVersionSchema } from '../../legal/accept-consent';
import { GenderSchema } from '../../users/gender';
import { DateOfBirthSchema } from '../../users/user';

import { EmailSchema, PasswordSchema } from '../password';

/**
 * **Structural step 0 only** (A4). Registration collects the five facts the
 * platform cannot function without — identity, gender, age, language — plus the
 * legal versions being accepted, and nothing else. Everything a matrimony
 * product would normally ask at signup (nationality, marital status, Civil
 * Registry ID, …) is a **registration-flow question** defined by staff in the
 * question engine (P6) and rendered by the client from the API, so the signup
 * form changes without a release.
 *
 * `.strict()` (N7): an extra key is a client bug or an injection attempt, and
 * either way silently ignoring it is worse than a 422.
 */
export const RegisterSchema = z
  .object({
    email: EmailSchema,
    password: PasswordSchema,
    gender: GenderSchema,
    dob: DateOfBirthSchema,
    locale: LocaleSchema,
    /**
     * The legal versions the registrant ticked (N16,
     * `03-legal-consent.md` §consent records) — every currently published
     * document that requires consent, fetched from `GET /legal/documents`.
     *
     * **Required and unpluralizable-away**: the api refuses a registration that
     * does not cover every required document, and it checks that *before*
     * creating the account, so a bad payload never leaves a user row behind. An
     * empty array is legitimate and is what a deployment with no published
     * documents sends — the check is coverage, not count.
     */
    acceptedLegalVersionIds: z.array(UuidSchema).max(50),
    /** Recorded on each consent row as evidence; the ip is taken server-side. */
    appVersion: AppVersionSchema.optional(),
  })
  .strict();

export type RegisterRequest = z.infer<typeof RegisterSchema>;

// The minimum age is NOT declared here. It is operator policy, resolved by the
// api through `getSetting('registration.minAge')` (U9) — a constant in a shared
// library would be a second source of truth that a client could enforce a
// different answer from. The registry holds the coded default (18).
